import FingerprintJS, { Component } from '@fingerprintjs/fingerprintjs';

let componentes: any = null;

function userAgentData(): Component<any> {
  const nav = navigator as any;
  const userAgentData = nav?.userAgentData;
  const ua = userAgentData ? {
    appVersion: nav.appVersion,
    mobile: userAgentData.mobile,
    platform: userAgentData.platform,
  } : navigator.userAgent;
  return {
    value: ua,
    duration: 0,
  };
}

function webGlCustomData(): Component<any> {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as any;

    if (!gl) {
      return { value: null, duration: 0 };
    }

    // Obtener información de dispositivos de renderizado
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const renderDevices = {
      unmaskedVendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : null,
      unmaskedRenderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null
    };

    // Información adicional de capacidades de renderizado
    const renderingCapabilities = {
      maxColorAttachments: gl.getParameter(gl.MAX_COLOR_ATTACHMENTS),
      maxDrawBuffers: gl.getParameter(gl.MAX_DRAW_BUFFERS),
      maxFragmentUniformVectors: gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
      maxVaryingVectors: gl.getParameter(gl.MAX_VARYING_VECTORS),
      maxVertexUniformVectors: gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
      maxCubeMapTextureSize: gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE),
      maxFramebufferWidth: gl.getParameter(gl.MAX_FRAMEBUFFER_WIDTH),
      maxFramebufferHeight: gl.getParameter(gl.MAX_FRAMEBUFFER_HEIGHT),
      maxSamples: gl.getParameter(gl.MAX_SAMPLES)
    };

    const webglInfo = {
      vendor: gl.getParameter(gl.VENDOR),
      renderer: gl.getParameter(gl.RENDERER),
      version: gl.getParameter(gl.VERSION),
      shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS),
      maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
      maxTextureImageUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
      maxVertexTextureImageUnits: gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),
      maxCombinedTextureImageUnits: gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS),
      maxVertexAttribs: gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
      aliasedLineWidthRange: gl.getParameter(gl.ALIASED_LINE_WIDTH_RANGE),
      aliasedPointSizeRange: gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE),
      extensions: gl.getSupportedExtensions(),
      renderDevices: renderDevices,
      renderingCapabilities: renderingCapabilities
    };

    return {
      value: webglInfo,
      duration: 0,
    };
  } catch (error) {
    return {
      value: { error: 'WebGL not available' },
      duration: 0,
    };
  }
}

function cpuHardwareFingerprint(): Component<any> {
  try {
    // Número físico de cores del CPU
    const hardwareConcurrency = navigator.hardwareConcurrency || 0;

    // Precision de floating point del CPU
    const floatTest = (0.1 + 0.2).toString();

    const cpuInfo = {
      cores: hardwareConcurrency,
      floatPrecision: floatTest,
      architecture: navigator.platform
    };

    return {
      value: cpuInfo,
      duration: 0,
    };
  } catch (error) {
    return {
      value: { error: 'CPU info not available' },
      duration: 0,
    };
  }
}

function gpuHardwareFingerprint(): Component<any> {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as any;

    if (!gl) {
      return { value: { error: 'GPU not available' }, duration: 0 };
    }

    // Información inmutable del hardware GPU
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

    const gpuInfo = {
      vendor: gl.getParameter(gl.VENDOR),
      renderer: gl.getParameter(gl.RENDERER),
      unmaskedVendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : null,
      unmaskedRenderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null,
      version: gl.getParameter(gl.VERSION),
      shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
      // Capacidades físicas del hardware
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      maxCubeMapTextureSize: gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE),
      maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
      maxVertexAttribs: gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
      // Precision del shader (específica del hardware)
      vertexShaderPrecision: {
        highFloat: gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.HIGH_FLOAT),
        mediumFloat: gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.MEDIUM_FLOAT),
        lowFloat: gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.LOW_FLOAT)
      }
    };

    return {
      value: gpuInfo,
      duration: 0,
    };
  } catch (error) {
    return {
      value: { error: 'GPU info not available' },
      duration: 0,
    };
  }
}

function memoryArchitectureFingerprint(): Component<any> {
  try {
    // RAM física total del sistema
    const deviceMemory = (navigator as any).deviceMemory || null;

    const memoryInfo2 = {
      deviceMemoryGB: deviceMemory,
    };

    return {
      value: memoryInfo2,
      duration: 0,
    };
  } catch (error) {
    return {
      value: { error: 'Memory info not available' },
      duration: 0,
    };
  }
}

function audioHardwareFingerprint(): Component<any> {
  try {
    const AudioContext = (window as any).AudioContext || (window as any).webkitAudioContext;
    
    if (!AudioContext) {
      return { value: { error: 'AudioContext not available' }, duration: 0 };
    }

    const audioCtx = new AudioContext();
    
    // Características inmutables del hardware de audio
    const audioInfo = {
      // Sample rate del hardware
      sampleRate: audioCtx.sampleRate,
      // Capacidades del hardware
      maxChannelCount: audioCtx.destination.maxChannelCount,
      numberOfInputs: audioCtx.destination.numberOfInputs,
      numberOfOutputs: audioCtx.destination.numberOfOutputs,
      channelCount: audioCtx.destination.channelCount,
      channelCountMode: audioCtx.destination.channelCountMode,
      channelInterpretation: audioCtx.destination.channelInterpretation
    };

    // Cerrar el contexto para liberar recursos
    audioCtx.close();

    return {
      value: audioInfo,
      duration: 0,
    };
  } catch (error) {
    return {
      value: { error: 'Audio hardware not available' },
      duration: 0,
    };
  }
}

function mathPrecisionFingerprint(): Component<any> {
  try {
    // Características matemáticas determinísticas del CPU
    const mathInfo = {
      // Precisión de floating point específica del hardware
      floatPrecision: (0.1 + 0.2).toString(),
      floatPrecision2: (0.3 - 0.1).toString(),
      floatPrecision3: (1.0 / 3.0).toString().substring(0, 20),
      
      // Constantes matemáticas del sistema
      mathConstants: {
        pi: Math.PI.toString().substring(0, 20),
        e: Math.E.toString().substring(0, 20),
        ln2: Math.LN2.toString().substring(0, 20),
        ln10: Math.LN10.toString().substring(0, 20),
        log2e: Math.LOG2E.toString().substring(0, 20),
        log10e: Math.LOG10E.toString().substring(0, 20),
        sqrt1_2: Math.SQRT1_2.toString().substring(0, 20),
        sqrt2: Math.SQRT2.toString().substring(0, 20)
      },
      
      // Límites numéricos del sistema
      numberLimits: {
        maxSafeInteger: Number.MAX_SAFE_INTEGER,
        minSafeInteger: Number.MIN_SAFE_INTEGER,
        maxValue: Number.MAX_VALUE.toString().substring(0, 20),
        minValue: Number.MIN_VALUE.toString().substring(0, 20),
        epsilon: Number.EPSILON.toString().substring(0, 20),
      },
      
      // Operaciones matemáticas determinísticas
      mathOperations: {
        sqrt2: Math.sqrt(2).toString().substring(0, 20),
        sqrt3: Math.sqrt(3).toString().substring(0, 20),
        sin1: Math.sin(1).toString().substring(0, 20),
        cos1: Math.cos(1).toString().substring(0, 20),
        tan1: Math.tan(1).toString().substring(0, 20),
        exp1: Math.exp(1).toString().substring(0, 20),
        log2: Math.log(2).toString().substring(0, 20)
      }
    };

    return {
      value: mathInfo,
      duration: 0,
    };
  } catch (error) {
    return {
      value: { error: 'Math precision not available' },
      duration: 0,
    };
  }
}

const getComponents = async () => {
  if (componentes) return componentes;
  const agent = await FingerprintJS.load();
  const allComponents = (await agent.get()).components;

  const stableComponentKeys = [
    'hardwareConcurrency',
    'deviceMemory',
    'platform',
    'timezone',
    'vendor',
    'architecture',
    'osCpu',
    'math'
  ];

  componentes = {};
  stableComponentKeys.forEach(key => {
    if (allComponents[key] !== undefined) {
      componentes[key] = allComponents[key];
    }
  });

  componentes.uaData = userAgentData();
  componentes.webGlCustom = webGlCustomData();
  componentes.cpuHardware = cpuHardwareFingerprint();
  componentes.gpuHardware = gpuHardwareFingerprint();
  componentes.memoryArchitecture = memoryArchitectureFingerprint();
  componentes.audioHardware = audioHardwareFingerprint();
  componentes.mathPrecision = mathPrecisionFingerprint();
  console.log('[SESSION] components', componentes);

  return componentes;
};

export const getFingerprint = async (): Promise<string> => {
  const components = await getComponents();
  const visitorId = FingerprintJS.hashComponents(components);
  return visitorId;
}; 